// M004-F0649 - the plan editor "Zones" section (design §3.7; Core M005-D004
// §10.14 W1-W4, Core MR !938, M005-F0085).
//
// A zone is a booking window linked to a plan with `applies_only_within`:
// the plan then applies only when a booking lies fully inside the zone
// (Z3). Each zone = name, day toggles, From/To, and the switch "Only applies
// inside this zone". Saves go through the window endpoints (create/patch) and
// the plan-link endpoint (PUT/DELETE) as ONE savePlanZones() that a SAL-4
// step-up REPLAYS. The client does no pricing maths; the copy only states the
// owner's rule (a zoned plan never makes a booking dearer: the cheapest
// applicable plan wins).
//
// Impact (Core 7fa446e2, verified against its real serializer output): the
// zone writes accept `?dry_run=true` and answer `propagation:{to_reprice, held,
// refused_billed, ...}` without writing. Before Save this section dry-runs each
// change to an EXISTING zone/link and sums the counts (labelled "up to" when
// several changes could touch the same booking). A brand-new zone has no window
// to dry-run yet, so its count is the one Core reports on the real write, which
// the saved notice prints. Zones inherited from the plan's template
// (`inherited: true`) are shown read-only: they belong to the template plan, and
// Core answers 404 "That zone is not linked to that plan." if one is unlinked
// here. No grandfather field is sent (the election lives on the plan edit).
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { pricePlanApi } from '../../services/pricePlanApi';
import { DAY_LABELS, daysFromMask, serverMessage } from '../admin/memberPricePlans/planModel';
import { normaliseZone, sumPropagation } from '../../utils/priceWire';
import './PlanZonesSection.css';

export const PLAN_ZONES_TESTID = 'price-plan-zones';

const maskFromDays = (days) => days.reduce((mask, on, index) => (on ? mask + (1 << index) : mask), 0);

let tempSeq = 0;
const newZoneDraft = () => {
  tempSeq += 1;
  return {
    tempId: `new-zone-${tempSeq}`, windowId: null, name: '', days: DAY_LABELS.map((_, i) => i === 5), start: '10:00', end: '16:00', within: true, isNew: true,
  };
};
const fromServerZone = (rawZone) => {
  const zone = normaliseZone(rawZone);
  return {
    tempId: `zone-${zone.window_id}`,
    windowId: zone.window_id,
    name: zone.name,
    days: daysFromMask(zone.weekday_mask),
    start: zone.start_local,
    end: zone.end_local,
    within: zone.applies_only_within === true,
    inherited: zone.inherited === true,
    isNew: false,
  };
};

const zoneFieldsEqual = (a, b) => a.name === b.name && a.start === b.start && a.end === b.end
  && maskFromDays(a.days) === maskFromDays(b.days);

const PlanZonesSection = ({
  organisationId, plan, canManage, onSaved,
}) => {
  const allServerZones = useMemo(() => (Array.isArray(plan?.zones) ? plan.zones : []).map(fromServerZone), [plan]);
  // Own zones are editable; inherited ones (from the template plan) are listed read-only.
  const serverZones = useMemo(() => allServerZones.filter((z) => !z.inherited), [allServerZones]);
  const inheritedZones = useMemo(() => allServerZones.filter((z) => z.inherited), [allServerZones]);
  const [zones, setZones] = useState(serverZones);
  const [removedIds, setRemovedIds] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);
  const [stepUp, setStepUp] = useState(null);
  const progressRef = useRef({});
  const draftRef = useRef({ zones, removedIds });
  draftRef.current = { zones, removedIds };

  // The draft mirrors the SELECTED plan as last loaded (a reload re-seeds it) - but NEVER over unsaved edits.
  // After a save the hub re-reads the plans, and that read hands this section a NEW `plan` object. An edit the
  // user makes while the read is in flight (Remove a zone, change a time) must survive it: re-seed only when the
  // draft is clean against the fresh server zones (or a different plan was selected). Pending edits are kept;
  // removals of zones the server no longer has are dropped (main 12590/12592: a Remove clicked during the
  // re-read was silently discarded and the following Save sent nothing).
  // The re-seed runs DURING RENDER, not in an effect. An effect reads the last RENDERED draft (draftRef), so a Remove
  // that was clicked but not yet rendered when the effect flushed was judged "clean" and overwritten: the zone came
  // back and Save sent no DELETE (T-M004-0084 s2, s2d). In render, `zones` and `removedIds` already include every
  // queued edit, and the re-seed commits in the same pass as the new plan, so the notice never paints ahead of the rows.
  const [seed, setSeed] = useState({ planId: plan?.id, serverZones });
  if (seed.serverZones !== serverZones) {
    const samePlan = seed.planId === plan?.id;
    const fresh = new Map(serverZones.map((z) => [z.windowId, z]));
    const hasUnsavedEdits = samePlan && (
      removedIds.some((id) => fresh.has(id))
      || zones.some((z) => z.isNew || !fresh.has(z.windowId)
        || !zoneFieldsEqual(z, fresh.get(z.windowId)) || z.within !== fresh.get(z.windowId).within)
    );
    setSeed({ planId: plan?.id, serverZones });
    if (hasUnsavedEdits) {
      setRemovedIds(removedIds.filter((id) => fresh.has(id)));
    } else {
      setZones(serverZones);
      setRemovedIds([]);
      setError('');
      progressRef.current = {};
    }
  }

  const originalById = useMemo(() => new Map(serverZones.map((z) => [z.windowId, z])), [serverZones]);
  const dirty = removedIds.length > 0
    || zones.some((z) => z.isNew || !originalById.has(z.windowId)
      || !zoneFieldsEqual(z, originalById.get(z.windowId)) || z.within !== originalById.get(z.windowId).within);

  // Dry runs of the changes to EXISTING zones/links (Core: ?dry_run=true). Each op is described by a
  // plain key so the effect re-runs exactly when the draft's meaning changes.
  const previewOps = useMemo(() => {
    const ops = [];
    removedIds.forEach((windowId) => ops.push({ kind: 'unlink', windowId }));
    zones.forEach((zone) => {
      const original = zone.windowId !== null ? originalById.get(zone.windowId) : null;
      if (zone.isNew || !original) return;
      if (!zoneFieldsEqual(zone, original)) {
        ops.push({
          kind: 'patch',
          windowId: zone.windowId,
          body: {
            name: zone.name.trim(), weekday_mask: maskFromDays(zone.days), start_local: zone.start, end_local: zone.end,
          },
        });
      }
      if (original.within !== zone.within) ops.push({ kind: 'link', windowId: zone.windowId, within: zone.within });
    });
    return ops;
  }, [zones, removedIds, originalById]);
  const hasNewZone = zones.some((z) => z.isNew);
  const previewKey = JSON.stringify(previewOps);

  useEffect(() => {
    let cancelled = false;
    setImpact(null);
    if (!dirty || !plan?.id || !organisationId) return undefined;
    if (previewOps.length === 0) {
      setImpact({
        toReprice: 0, held: 0, refusedBilled: 0, approximate: false,
      });
      return undefined;
    }
    const run = (op) => {
      if (op.kind === 'unlink') return pricePlanApi.deletePlanWindowLink(organisationId, plan.id, op.windowId, { dryRun: true });
      if (op.kind === 'patch') return pricePlanApi.patchWindow(organisationId, op.windowId, op.body, { dryRun: true });
      return pricePlanApi.putPlanWindowLink(organisationId, plan.id, op.windowId, { price_mode: 'plan_hourly', applies_only_within: op.within }, { dryRun: true });
    };
    Promise.all(previewOps.map(run))
      .then((responses) => {
        if (cancelled) return;
        const blocks = responses.map((response) => (response?.data?.data ?? response?.data)?.propagation);
        setImpact({ ...sumPropagation(blocks), approximate: previewOps.length > 1 });
      })
      .catch((err) => {
        if (cancelled) return;
        // Core's dry run sits behind the same SAL-4 step-up as the write: without a fresh step-up the
        // exact count is not available yet. That must not block Save (Save opens the step-up and replays).
        setImpact(getStepUpChallengeFromError(err)?.id ? { needsStepUp: true } : false);
      });
    return () => { cancelled = true; };
  }, [dirty, organisationId, plan?.id, previewKey]);

  const update = (tempId, patch) => setZones((prev) => prev.map((z) => (z.tempId === tempId ? { ...z, ...patch } : z)));
  const toggleDay = (zone, index) => update(zone.tempId, { days: zone.days.map((on, i) => (i === index ? !on : on)) });
  const removeZone = (zone) => {
    setZones((prev) => prev.filter((z) => z.tempId !== zone.tempId));
    if (!zone.isNew && zone.windowId !== null) setRemovedIds((prev) => [...prev, zone.windowId]);
  };

  const validationFor = (zone) => {
    if (!zone.name.trim()) return 'Give the zone a name.';
    if (!zone.days.some(Boolean)) return 'Pick at least one day.';
    if (!zone.start || !zone.end) return 'Set a start and end time.';
    if (zone.end <= zone.start) return 'A zone must end after it starts.';
    return '';
  };

  const savePlanZones = useCallback(async () => {
    const { zones: draftZones, removedIds: draftRemoved } = draftRef.current;
    const progress = progressRef.current;
    const propagation = progress.propagation || (progress.propagation = []);
    const keepPropagation = (response) => {
      const block = (response?.data?.data ?? response?.data)?.propagation;
      if (block) propagation.push(block);
    };
    setSaving(true);
    setError('');
    try {
      for (const windowId of draftRemoved) {
        const key = `unlink:${windowId}`;
        if (progress[key]) continue;
        keepPropagation(await pricePlanApi.deletePlanWindowLink(organisationId, plan.id, windowId));
        progress[key] = true;
      }
      for (const zone of draftZones) {
        const original = zone.windowId !== null ? originalById.get(zone.windowId) : null;
        let windowId = zone.windowId ?? progress[`created:${zone.tempId}`] ?? null;
        if (zone.isNew && windowId === null) {
          const response = await pricePlanApi.createWindow(organisationId, {
            name: zone.name.trim(),
            weekday_mask: maskFromDays(zone.days),
            start_local: zone.start,
            end_local: zone.end,
            booking_shape: 'envelope',
            access_mode: 'open',
          });
          windowId = (response?.data?.data ?? response?.data)?.window?.id;
          progress[`created:${zone.tempId}`] = windowId;
        } else if (original && !zoneFieldsEqual(zone, original) && !progress[`patched:${windowId}`]) {
          keepPropagation(await pricePlanApi.patchWindow(organisationId, windowId, {
            name: zone.name.trim(), weekday_mask: maskFromDays(zone.days), start_local: zone.start, end_local: zone.end,
          }));
          progress[`patched:${windowId}`] = true;
        }
        const needsLink = zone.isNew || !original || original.within !== zone.within;
        if (needsLink && !progress[`linked:${windowId}`]) {
          keepPropagation(await pricePlanApi.putPlanWindowLink(organisationId, plan.id, windowId, {
            price_mode: 'plan_hourly', applies_only_within: zone.within,
          }));
          progress[`linked:${windowId}`] = true;
        }
      }
      const done = sumPropagation(propagation);
      progressRef.current = {};
      // What this save wrote is no longer an unsaved edit: forget the removals it sent and the drafts it created
      // (the re-read brings them back with their server ids), so only edits made AFTER the save count as pending.
      setRemovedIds((prev) => prev.filter((id) => !draftRemoved.includes(id)));
      setZones((prev) => prev.filter((z) => !draftZones.some((d) => d.tempId === z.tempId && d.isNew)));
      const message = `Zones saved. Core repriced ${done.toReprice} upcoming booking${done.toReprice === 1 ? '' : 's'}`
        + `${done.refusedBilled > 0 ? `; ${done.refusedBilled} already billed stay unchanged` : ''}.`;
      setNotice(message);
      // The hub tab swaps the whole editor for its loading state while it
      // re-reads the plans (this section remounts), so the confirmation is
      // handed to the hub, which owns the notice that survives the reload.
      if (onSaved) await onSaved(message);
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setStepUp({ challenge });
        setError('A fresh step-up (SAL 4) is needed before zones can be saved. Your changes are kept.');
      } else {
        setError(serverMessage(err, 'Could not save the zones.'));
      }
    } finally {
      setSaving(false);
    }
  }, [organisationId, plan, originalById, onSaved]);

  const onSave = () => {
    const first = zones.map(validationFor).find(Boolean);
    if (first) { setError(first); return; }
    setNotice('');
    savePlanZones();
  };

  const completeStepUp = async () => { setStepUp(null); await savePlanZones(); };

  if (!plan) return null;
  return (
    <section className="pzs-panel" data-testid={PLAN_ZONES_TESTID} aria-label="Zones">
      <h4>Zones</h4>
      <p className="pzs-muted">
        A zoned plan applies only when a booking sits fully inside one of its zones. It never makes a booking dearer:
        the cheapest applicable plan always wins.
      </p>
      {!canManage ? (
        <p className="pzs-muted" data-testid="plan-zones-readonly" title="Requires the pricing_plans_manage permission.">
          View only {'—'} zones need the pricing_plans_manage permission to change.
        </p>
      ) : null}
      {inheritedZones.map((zone) => (
        <div className="pzs-zone pzs-zone-inherited" key={zone.tempId} data-testid="plan-zone-inherited" data-window-id={zone.windowId}>
          <div className="pzs-row">
            <b data-testid="plan-zone-inherited-name">{zone.name}</b>
            <span className="pzs-muted" data-testid="plan-zone-inherited-note">Inherited from the template plan {'—'} change it on the template.</span>
          </div>
          <div className="pzs-row">
            <span data-testid="plan-zone-inherited-days">{DAY_LABELS.filter((_, i) => zone.days[i]).join(', ')}</span>
            <span data-testid="plan-zone-inherited-times">{zone.start}{'–'}{zone.end}</span>
            <span className="pzs-muted">{zone.within ? 'Only applies inside this zone' : 'Does not restrict the plan'}</span>
          </div>
        </div>
      ))}
      {zones.length === 0 && inheritedZones.length === 0 ? <p className="pzs-muted" data-testid="plan-zones-empty">This plan has no zones, so it applies at any time.</p> : null}
      {zones.map((zone) => (
        <div className="pzs-zone" key={zone.tempId} data-testid="plan-zone" data-window-id={zone.windowId ?? ''}>
          <div className="pzs-row">
            <input
              type="text"
              className="pzs-input pzs-name"
              aria-label="Zone name"
              placeholder="Zone name (e.g. Sat workshop)"
              value={zone.name}
              disabled={!canManage}
              onChange={(event) => update(zone.tempId, { name: event.target.value })}
              data-testid="plan-zone-name"
            />
            {canManage ? (
              <button type="button" className="pzs-btn pzs-btn-outline pzs-btn-sm" onClick={() => removeZone(zone)} data-testid="plan-zone-remove">
                Remove zone
              </button>
            ) : null}
          </div>
          <div className="pzs-days" role="group" aria-label="Days">
            {DAY_LABELS.map((label, index) => (
              <button
                type="button"
                key={label}
                className="pzs-day"
                aria-pressed={zone.days[index]}
                disabled={!canManage}
                onClick={() => toggleDay(zone, index)}
                data-testid="plan-zone-day"
                data-day={label}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="pzs-row">
            <label>
              From{' '}
              <input type="time" className="pzs-input" value={zone.start} disabled={!canManage} onChange={(event) => update(zone.tempId, { start: event.target.value })} data-testid="plan-zone-from" />
            </label>
            <label>
              To{' '}
              <input type="time" className="pzs-input" value={zone.end} disabled={!canManage} onChange={(event) => update(zone.tempId, { end: event.target.value })} data-testid="plan-zone-to" />
            </label>
          </div>
          <label className="pzs-switch">
            <input
              type="checkbox"
              role="switch"
              checked={zone.within}
              disabled={!canManage}
              onChange={(event) => update(zone.tempId, { within: event.target.checked })}
              data-testid="plan-zone-within"
            />
            {' '}Only applies inside this zone
          </label>
        </div>
      ))}

      {impact?.needsStepUp ? (
        <p className="pzs-muted" data-testid="plan-zones-impact-stepup">
          The exact number of bookings this reprices is shown by Core once you confirm with a fresh step-up (SAL 4); Save asks for it.
        </p>
      ) : null}
      {impact && !impact.needsStepUp ? (
        <p className="pzs-impact" data-testid="plan-zones-impact">
          Saving reprices {impact.approximate ? 'up to ' : ''}{impact.toReprice} upcoming booking{impact.toReprice === 1 ? '' : 's'}; {impact.refusedBilled} already billed stay unchanged.
          {hasNewZone ? ' A new zone is priced when it is saved; Core then reports how many bookings it repriced.' : ''}
        </p>
      ) : null}
      {impact === false && dirty ? (
        <p className="pzs-error" role="alert" data-testid="plan-zones-impact-error">
          The impact on upcoming bookings could not be checked, so zones cannot be saved right now.
        </p>
      ) : null}
      {error ? <p className="pzs-error" role="alert" data-testid="plan-zones-error">{error}</p> : null}
      {notice ? <p className="pzs-notice" role="status" data-testid="plan-zones-notice">{notice}</p> : null}

      {canManage ? (
        <div className="pzs-row">
          <button type="button" className="pzs-btn pzs-btn-outline" onClick={() => setZones((prev) => [...prev, newZoneDraft()])} data-testid="plan-zone-add">
            + Add zone
          </button>
          <button type="button" className="pzs-btn pzs-btn-primary" disabled={!dirty || saving || impact === false} onClick={onSave} data-testid="plan-zones-save">
            {saving ? 'Saving…' : 'Save zones'}
          </button>
        </div>
      ) : null}

      <StepUpDialog
        challenge={stepUp?.challenge || null}
        title="Confirm it's you"
        description="Changing a plan's zones needs a fresh step-up (SAL 4). Your save will replay automatically once verified."
        onComplete={completeStepUp}
        onCancel={() => { setStepUp(null); setError('Step-up cancelled. Nothing was saved; your changes are kept.'); }}
      />
    </section>
  );
};

export default PlanZonesSection;
